import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import { useLang, type Key } from '../i18n'
import { useAvailableTours } from '../tours/registry'

interface HelpItem {
  key: string
  label: Key
  /** Why the entry is unavailable — a `title` tooltip plus a visually-hidden aria-describedby text. */
  disabledReason: Key | null
  act: () => void
}

/**
 * The `?` Help menu of the top bar, next to the EN/FR toggle (docs/spec/62-help.md):
 *
 *  - "User guide" — not built yet, always aria-disabled with "Coming soon";
 *  - "Guided tours" — aria-disabled with "No guided tour is available yet" while no tour is
 *    available to the user (useAvailableTours), else it calls `onOpenGuidedTours`.
 *
 * Each entry is one line, styled like the account menu's links (TopBar.tsx); a disabled entry's
 * reason is its tooltip and, for screen readers, its accessible description.
 *
 * Same idiom as the account menu (TopBar.tsx) and ExportMenu: Escape / outside click close (Escape
 * returns focus to the trigger), and every item — disabled ones included, so their reason can be
 * read — is in one roving-tabindex cycle (ArrowUp/ArrowDown/Home/End).
 */
export function HelpMenu({ onOpenGuidedTours }: { onOpenGuidedTours: () => void }) {
  const { t } = useLang()
  const tours = useAvailableTours()
  const menuId = useId()
  const idBase = useId()
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)

  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const itemsRef = useRef<Array<HTMLButtonElement | null>>([])

  const items: HelpItem[] = [
    { key: 'user-guide', label: 'help_user_guide', disabledReason: 'help_coming_soon', act: () => {} },
    {
      key: 'guided-tours',
      label: 'help_guided_tours',
      disabledReason: tours.length === 0 ? 'help_no_tour_yet' : null,
      act: onOpenGuidedTours,
    },
  ]
  const itemCount = items.length

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false)
    if (returnFocus) triggerRef.current?.focus()
  }, [])

  useEffect(() => {
    if (open) itemsRef.current[active]?.focus()
  }, [open, active])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  function onMenuKeyDown(e: KeyboardEvent): void {
    switch (e.key) {
      case 'Escape':
        e.preventDefault()
        close(true)
        break
      case 'ArrowDown':
        e.preventDefault()
        setActive((i) => (i + 1) % itemCount)
        break
      case 'ArrowUp':
        e.preventDefault()
        setActive((i) => (i - 1 + itemCount) % itemCount)
        break
      case 'Home':
        e.preventDefault()
        setActive(0)
        break
      case 'End':
        e.preventDefault()
        setActive(itemCount - 1)
        break
    }
  }

  function openMenu(): void {
    setActive(0)
    setOpen(true)
  }

  return (
    <div className="help-menu" ref={rootRef}>
      <button
        ref={triggerRef}
        className={`help-btn${open ? ' on' : ''}`}
        data-help-id="topbar-help"
        aria-label={t('help_menu')}
        title={t('help_menu')}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? close(false) : openMenu())}
      >
        <span aria-hidden="true">?</span>
      </button>

      {open && (
        <div id={menuId} className="export-pop" role="menu" aria-label={t('help_menu')} onKeyDown={onMenuKeyDown}>
          {items.map((item, i) => {
            const labelId = `${idBase}-${item.key}`
            const reasonId = `${labelId}-why`
            const disabled = item.disabledReason !== null
            return (
              <button
                key={item.key}
                ref={(el) => { itemsRef.current[i] = el }}
                className="export-item help-item"
                role="menuitem"
                tabIndex={active === i ? 0 : -1}
                aria-disabled={disabled || undefined}
                aria-labelledby={labelId}
                aria-describedby={disabled ? reasonId : undefined}
                title={item.disabledReason ? t(item.disabledReason) : undefined}
                onClick={() => {
                  if (disabled) return
                  close(false)
                  item.act()
                }}
              >
                <span id={labelId}>{t(item.label)}</span>
                {item.disabledReason && (
                  <span id={reasonId} className="visually-hidden">{t(item.disabledReason)}</span>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
